/** @prettier */

import {useEffect, useState} from 'react';
import {utcToZonedTime} from 'date-fns-tz';
import {differenceInCalendarDays, format, sub} from 'date-fns';
import {getStatesSitesGroups} from '../../API/API_Collection';
import * as apiIF from '../../Type/api';
import {graph} from '../../define';

export const useGroupChangeStartEndDate = (
  siteID: string | undefined,
  barGraphStartDate: string,
  barGraphEndDate: string,
  graphType: string,
  timeZone: string,
  dateRange: string
) => {
  const [apiWait, setApiWait] = useState(false); //API待機中判定
  const [trendDisable, setTrendDisable] = useState(false); //トレンドグラフ選択可否
  const [powerDisable, setPowerDisable] = useState(false); //電力グラフ選択可否
  const [graphDispError, setGraphDispError] = useState(false); //
  const [fileNameDate, setFileNameDate] = useState(''); //
  //prettier-ignore
  const [groupData, setGroupData] = useState<apiIF.res.getStatesSitesGroups | null>(null); //グループ単位の運転データ

  /* グラフボタン押下、他ユニットのグラフを表示、カレンダ日付変更に日時を更新し、グラフ描画用データを取得する */
  useEffect(() => {
    if (
      siteID === undefined ||
      barGraphStartDate === '' ||
      barGraphEndDate === ''
    )
      return;
    setApiWait(true);
    setTrendDisable(true);
    setPowerDisable(true);

    //APIに渡すクエリの決定
    let fields = '';
    //prettier-ignore
    if (graphType === 'line') fields = 'indoor-temperature,outdoor-temperature,preset-temperature';
    if (graphType === 'bar') fields = 'power-consumption';
    const afterDate = barGraphStartDate;
    const beforeDate = barGraphEndDate;
    let everyMinute;
    let maxResult;

    //グラフタイトルに表示する日付情報(開始日)を作成
    const start = format(
      utcToZonedTime(barGraphStartDate, timeZone),
      'dd.MMMM yyyy'
    );
    let csvTitle; // csvに表示する日時

    switch (dateRange) {
      case 'day':
      default:
        everyMinute =
          graphType === 'line'
            ? graph.DAY_LINE_DATE_RANGE
            : graph.DAY_BAR_DATE_RANGE;
        maxResult =
          graphType === 'line'
            ? graph.DAY_LINE_API_MAX_RESULT
            : graph.DAY_BAR_API_MAX_RESULT;
        csvTitle = format(
          utcToZonedTime(barGraphStartDate, timeZone),
          'yyyyMMdd'
        );
        break;
      case 'week':
        everyMinute =
          graphType === 'line'
            ? graph.WEEK_LINE_DATE_RANGE
            : graph.WEEK_BAR_DATE_RANGE;
        maxResult =
          graphType === 'line'
            ? graph.WEEK_LINE_API_MAX_RESULT
            : graph.WEEK_BAR_API_MAX_RESULT;
        //1週間表示のcsvファイルに出力する日付情報を作成
        const tempCSVTitleStart = format(
          utcToZonedTime(barGraphStartDate, timeZone),
          'yyyyMMdd'
        );
        const tempCSVTitleEnd = format(
          utcToZonedTime(sub(new Date(barGraphEndDate), {days: 1}), timeZone),
          'yyyyMMdd'
        );
        csvTitle = `${tempCSVTitleStart}_${tempCSVTitleEnd}`;
        break;
      case 'month':
        everyMinute = graph.MONTH_BAR_DATE_RANGE;
        maxResult = graph.MONTH_BAR_API_MAX_RESULT;
        const tempMonthCSVTitleStart = format(
          utcToZonedTime(barGraphStartDate, timeZone),
          'yyyyMM'
        );
        csvTitle = `${tempMonthCSVTitleStart}`;
        break;
      case 'year':
        everyMinute = graph.YEAR_BAR_DATE_RANGE;
        maxResult = graph.YEAR_BAR_API_MAX_RESULT;
        //1年表示のcsvファイルに出力する日付情報を作成
        const yearStart = format(
          utcToZonedTime(barGraphStartDate, timeZone),
          'yyyy'
        );
        csvTitle = `${yearStart}`;
        break;
      case 'tenYear':
        everyMinute = graph.TEN_YEAR_BAR_DATE_RANGE;
        maxResult = graph.TEN_YEAR_BAR_API_MAX_RESULT;
        //10年表示のcsvファイルに出力するグラフの開始年を作成
        const tenYearStart = format(
          utcToZonedTime(barGraphStartDate, timeZone),
          'yyyy'
        );
        //10年表示のcsvファイルに出力するグラフの終了年を作成
        const tenYearEnd = format(
          utcToZonedTime(sub(new Date(barGraphEndDate), {days: 1}), timeZone),
          'yyyy'
        );
        csvTitle = `${tenYearStart}_${tenYearEnd}`;
        break;
    }

    /* グラフボタンと日付変更が同時に発生した場合、useEffectが2回走り、APIの完了タイミングによって */
    /* day選択時にweekの結果を表示しようとするので、これを防ぐため下記条件を実装 */
    const distance = differenceInCalendarDays(
      new Date(barGraphEndDate),
      new Date(barGraphStartDate)
    );
    if (
      (dateRange === 'day' && distance < 2) ||
      (dateRange === 'week' && distance >= 2 && distance <= 7) ||
      (dateRange === 'month' && distance > 7 && distance <= 31) ||
      (dateRange === 'year' && distance > 31 && distance <= 366) ||
      (dateRange === 'tenYear' && distance > 366 && distance <= 3653)
    ) {
      setFileNameDate(csvTitle);

      setGraphDispError(false);
      getStatesSitesGroups(siteID, {
        'fields': fields,
        'after-time': afterDate,
        'before-time': beforeDate,
        'every-minute': everyMinute,
        'max-result': maxResult,
      })
        .then(response => {
          setGroupData(response);
        })
        .catch(() => {
          setGraphDispError(true);
          setTrendDisable(false);
          setPowerDisable(false);
        })
        .finally(() => {
          setApiWait(false);
        });
    }
  }, [graphType, barGraphStartDate, barGraphEndDate]);

  return {
    groupData,
    fileNameDate,
    trendDisable,
    powerDisable,
    graphDispError,
    apiWait,
  };
};
